// src/stores/CreatorStore.ts

import { makeAutoObservable, runInAction } from 'mobx';
import { 
  CreatorDashboard,
  CreatorAnalytics,
  PublicCatalog,
  Transaction,
  RevenueShare,
  LicenseRequest
} from '@/types/marketplace';
import { creatorService } from '../services/creatorService';
import { RootStore } from '.';

export class CreatorStore {
    rootStore: RootStore;
  // Creator dashboard data
  dashboard: CreatorDashboard | null = null;
  analytics: CreatorAnalytics | null = null;
  
  // Creator's content
  myContents: PublicCatalog[] = [];
  draftContents: PublicCatalog[] = [];
  publishedContents: PublicCatalog[] = [];
  
  // Revenue and earnings
  totalEarnings: number = 0;
  monthlyEarnings: number = 0;
  revenueShare: RevenueShare | null = null;
  payouts: Transaction[] = [];
  
  // License requests for creator's content
  incomingLicenseRequests: LicenseRequest[] = [];
  
  // Performance metrics
  topPerformingContent: PublicCatalog[] = [];
  recentSales: Transaction[] = [];
  
  // UI states
  isLoading: boolean = false;
  isUploading: boolean = false;
  error: string | null = null;
  
  // Pagination
  currentPage: number = 1;
  totalPages: number = 1;
  hasMore: boolean = true;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  // Initialization
  async initialize(creatorId: string) {
    this.setLoading(true);
    
    try {
      await Promise.all([
        this.loadDashboard(creatorId),
        this.loadMyContents(creatorId),
        this.loadIncomingRequests(creatorId),
        this.loadAnalytics(creatorId)
      ]);
    } catch (error) {
      this.setError('Không thể tải dữ liệu creator');
      console.error('Creator initialization error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  // Dashboard data
  async loadDashboard(creatorId: string) {
    try {
      const dashboard = await creatorService.getDashboard(creatorId);
      runInAction(() => {
        this.dashboard = dashboard;
        this.totalEarnings = dashboard.totalEarnings;
        this.monthlyEarnings = dashboard.thisMonthEarnings;
        this.topPerformingContent = dashboard.topPerformingContent;
        this.recentSales = dashboard.recentTransactions;
      });
    } catch (error) {
      console.error('Load dashboard error:', error);
      throw error;
    }
  }

  async loadAnalytics(creatorId: string) {
    try {
      const analytics = await creatorService.getAnalytics(creatorId);
      runInAction(() => {
        this.analytics = analytics;
      });
    } catch (error) {
      console.error('Load analytics error:', error);
    }
  }
  
  // Remove duplicate
  // async loadAnalytics(timeRange: TimeRange) {
  //   try {
  //     const response = await api.get('/analytics/creator', {
  //       params: {
  //         startDate: timeRange.start.toISOString(),
  //         endDate: timeRange.end.toISOString()
  //       }
  //     });
      
  //     this.analytics = response.data;
  //   } catch (error) {
  //     console.error('Failed to load analytics:', error);
  //   }
  // }

  async exportAnalytics(format: 'csv' | 'pdf' | 'xlsx') {
    const response = await api.get(`/analytics/export/${format}`, {
      responseType: 'blob'
    });
    
    // Download file
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `analytics.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
  async loadRevenueShare(creatorId: string, period?: { from: string; to: string }) {
    try {
      const revenueShare = await creatorService.getRevenueShare(creatorId, period);
      runInAction(() => {
        this.revenueShare = revenueShare;
      });
    } catch (error) {
      console.error('Load revenue share error:', error);
    }
  }

  // Content management
  async loadMyContents(creatorId: string) {
    try {
      const contents = await creatorService.getCreatorContents(creatorId);
      runInAction(() => {
        this.myContents = contents;
        this.draftContents = contents.filter(c => c.marketplaceStatus === 'inactive');
        this.publishedContents = contents.filter(c => c.marketplaceStatus === 'active');
      });
    } catch (error) {
      console.error('Load my contents error:', error);
      throw error;
    }
  }

  async publishContentToMarketplace(contentId: string, marketplaceData: any) {
    this.setUploading(true);
    this.setError(null);
    
    try {
      const publishedContent = await creatorService.publishToMarketplace(contentId, marketplaceData);
      
      runInAction(() => {
        // Update content in lists
        this.updateContentInLists(publishedContent);
        
        // Move from draft to published
        this.draftContents = this.draftContents.filter(c => c.documentId !== contentId);
        if (!this.publishedContents.find(c => c.documentId === contentId)) {
          this.publishedContents.push(publishedContent);
        }
      });
      
      return publishedContent;
    } catch (error) {
      this.setError('Không thể đăng content lên marketplace');
      console.error('Publish content error:', error);
      throw error;
    } finally {
      this.setUploading(false);
    }
  }

  async updateContentLicensing(contentId: string, licensingData: any) {
    this.setUploading(true);
    this.setError(null);
    
    try {
      const updatedContent = await creatorService.updateContentLicensing(contentId, licensingData);
      
      runInAction(() => {
        this.updateContentInLists(updatedContent);
      });
      
      return updatedContent;
    } catch (error) {
      this.setError('Không thể cập nhật thông tin license');
      console.error('Update content licensing error:', error);
      throw error;
    } finally {
      this.setUploading(false);
    }
  }

  async removeContentFromMarketplace(contentId: string) {
    this.setUploading(true);
    this.setError(null);
    
    try {
      await creatorService.removeFromMarketplace(contentId);
      
      runInAction(() => {
        // Remove from published and add to draft
        const content = this.publishedContents.find(c => c.documentId === contentId);
        if (content) {
          this.publishedContents = this.publishedContents.filter(c => c.documentId !== contentId);
          content.marketplaceStatus = 'inactive';
          this.draftContents.push(content);
        }
      });
    } catch (error) {
      this.setError('Không thể gỡ content khỏi marketplace');
      console.error('Remove content error:', error);
      throw error;
    } finally {
      this.setUploading(false);
    }
  }

  // License request management
  async loadIncomingRequests(creatorId: string) {
    try {
      const requests = await creatorService.getIncomingLicenseRequests(creatorId);
      runInAction(() => {
        this.incomingLicenseRequests = requests;
      });
    } catch (error) {
      console.error('Load incoming requests error:', error);
    }
  }

  async approveLicenseRequest(requestId: string, terms?: any) {
    this.setUploading(true);
    this.setError(null);
    
    try {
      const approvedRequest = await creatorService.approveLicenseRequest(requestId, terms);
      
      runInAction(() => {
        const index = this.incomingLicenseRequests.findIndex(r => r.documentId === requestId);
        if (index !== -1) {
          this.incomingLicenseRequests[index] = approvedRequest;
        }
      });
      
      return approvedRequest;
    } catch (error) {
      this.setError('Không thể phê duyệt yêu cầu license');
      console.error('Approve license request error:', error);
      throw error;
    } finally {
      this.setUploading(false);
    }
  }

  async rejectLicenseRequest(requestId: string, reason: string) {
    this.setUploading(true);
    this.setError(null);
    
    try {
      const rejectedRequest = await creatorService.rejectLicenseRequest(requestId, reason);
      
      runInAction(() => {
        const index = this.incomingLicenseRequests.findIndex(r => r.documentId === requestId);
        if (index !== -1) {
          this.incomingLicenseRequests[index] = rejectedRequest;
        }
      });
      
      return rejectedRequest;
    } catch (error) {
      this.setError('Không thể từ chối yêu cầu license');
      console.error('Reject license request error:', error);
      throw error;
    } finally {
      this.setUploading(false);
    }
  }

  // Payout management
  async loadPayouts(creatorId: string) {
    try {
      const payouts = await creatorService.getPayouts(creatorId);
      runInAction(() => {
        this.payouts = payouts;
      });
    } catch (error) {
      console.error('Load payouts error:', error);
    }
  }

  async requestPayout(amount: number, paymentMethod: string) {
    this.setUploading(true);
    this.setError(null);
    
    try {
      const payout = await creatorService.requestPayout(amount, paymentMethod);
      
      runInAction(() => {
        this.payouts.unshift(payout);
      });
      
      return payout;
    } catch (error) {
      this.setError('Không thể yêu cầu rút tiền');
      console.error('Request payout error:', error);
      throw error;
    } finally {
      this.setUploading(false);
    }
  }

  // Content optimization suggestions
  async getContentOptimizationSuggestions(contentId: string) {
    try {
      return await creatorService.getOptimizationSuggestions(contentId);
    } catch (error) {
      console.error('Get optimization suggestions error:', error);
      return [];
    }
  }

  async getMarketInsights(category?: string) {
    try {
      return await creatorService.getMarketInsights(category);
    } catch (error) {
      console.error('Get market insights error:', error);
      return null;
    }
  }

  // Bulk operations
  async bulkUpdatePricing(contentIds: string[], pricingData: any) {
    this.setUploading(true);
    this.setError(null);
    
    try {
      const updatedContents = await creatorService.bulkUpdatePricing(contentIds, pricingData);
      
      runInAction(() => {
        updatedContents.forEach(content => {
          this.updateContentInLists(content);
        });
      });
      
      return updatedContents;
    } catch (error) {
      this.setError('Không thể cập nhật giá hàng loạt');
      console.error('Bulk update pricing error:', error);
      throw error;
    } finally {
      this.setUploading(false);
    }
  }

  async bulkPublishContents(contentIds: string[], marketplaceData: any) {
    this.setUploading(true);
    this.setError(null);
    
    try {
      const publishedContents = await creatorService.bulkPublishToMarketplace(contentIds, marketplaceData);
      
      runInAction(() => {
        publishedContents.forEach(content => {
          this.updateContentInLists(content);
          // Move from draft to published
          this.draftContents = this.draftContents.filter(c => c.documentId !== content.documentId);
          if (!this.publishedContents.find(c => c.documentId === content.documentId)) {
            this.publishedContents.push(content);
          }
        });
      });
      
      return publishedContents;
    } catch (error) {
      this.setError('Không thể đăng hàng loạt content');
      console.error('Bulk publish contents error:', error);
      throw error;
    } finally {
      this.setUploading(false);
    }
  }

  // Helper methods
  private updateContentInLists(updatedContent: PublicCatalog) {
    // Update in main list
    const mainIndex = this.myContents.findIndex(c => c.documentId === updatedContent.documentId);
    if (mainIndex !== -1) {
      this.myContents[mainIndex] = updatedContent;
    }
    
    // Update in published list
    const publishedIndex = this.publishedContents.findIndex(c => c.documentId === updatedContent.documentId);
    if (publishedIndex !== -1) {
      this.publishedContents[publishedIndex] = updatedContent;
    }
    
    // Update in draft list
    const draftIndex = this.draftContents.findIndex(c => c.documentId === updatedContent.documentId);
    if (draftIndex !== -1) {
      this.draftContents[draftIndex] = updatedContent;
    }
  }

  // State management
  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setUploading(uploading: boolean) {
    this.isUploading = uploading;
  }

  setError(error: string | null) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  // Getters
  get pendingLicenseRequests() {
    return this.incomingLicenseRequests.filter(r => r.status === 'pending');
  }

  get totalPendingRequests() {
    return this.pendingLicenseRequests.length;
  }

  get totalContentCount() {
    return this.myContents.length;
  }

  get publishedContentCount() {
    return this.publishedContents.length;
  }

  get draftContentCount() {
    return this.draftContents.length;
  }

  get averageRating() {
    if (this.publishedContents.length === 0) return 0;
    
    const totalRating = this.publishedContents.reduce((sum, content) => 
      sum + content.averageRating, 0);
    return totalRating / this.publishedContents.length;
  }

  get totalViews() {
    return this.publishedContents.reduce((sum, content) => 
      sum + content.viewCount, 0);
  }

  get totalDownloads() {
    return this.publishedContents.reduce((sum, content) => 
      sum + content.downloadCount, 0);
  }

  get pendingPayoutAmount() {
    return this.payouts
      .filter(p => p.status === 'pending')
      .reduce((sum, p) => sum + p.amount, 0);
  }

  get thisMonthSales() {
    const currentMonth = new Date().toISOString().slice(0, 7);
    return this.recentSales.filter(sale => 
      sale.createdAt.startsWith(currentMonth)
    ).length;
  }

  get topPerformingCategory() {
    if (this.publishedContents.length === 0) return null;
    
    const categoryPerformance = this.publishedContents.reduce((acc, content) => {
      const category = content.category;
      const performance = content.viewCount + content.downloadCount;
      acc[category] = (acc[category] || 0) + performance;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(categoryPerformance)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || null;
  }

  // Analytics helpers
  getContentPerformanceMetrics() {
    return this.publishedContents.map(content => ({
      id: content.documentId,
      title: content.title,
      views: content.viewCount,
      downloads: content.downloadCount,
      rating: content.averageRating,
      revenue: this.getContentRevenue(content.documentId)
    }));
  }

  private getContentRevenue(contentId: string): number {
    return this.recentSales
      .filter(sale => sale.content?.documentId === contentId)
      .filter(sale => sale.status === 'completed')
      .reduce((sum, sale) => sum + sale.amount, 0);
  }

  getRevenueByMonth() {
    const monthlyRevenue = this.recentSales.reduce((acc, sale) => {
      if (sale.status !== 'completed') return acc;
      
      const month = new Date(sale.createdAt).toISOString().slice(0, 7);
      acc[month] = (acc[month] || 0) + sale.amount;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(monthlyRevenue)
      .map(([month, revenue]) => ({ month, revenue }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }

  getContentByCategoryPerformance() {
    const categoryStats = this.publishedContents.reduce((acc, content) => {
      const category = content.category;
      if (!acc[category]) {
        acc[category] = { count: 0, views: 0, downloads: 0, revenue: 0 };
      }
      acc[category].count += 1;
      acc[category].views += content.viewCount;
      acc[category].downloads += content.downloadCount;
      acc[category].revenue += this.getContentRevenue(content.documentId);
      return acc;
    }, {} as Record<string, any>);
    
    return Object.entries(categoryStats).map(([category, stats]) => ({
      category,
      ...stats
    }));
  }
}

export default CreatorStore;